import { useState } from "react";
import { useLocation } from "wouter";
import { useRegisterSW } from "virtual:pwa-register/react";
import { isVideoLivePath } from "@/lib/videoRoomPath";
import { applyPwaUpdate } from "@/lib/applyPwaUpdate";

/**
 * Баннер «Доступно обновление».
 *
 * Разделён на два компонента не для красоты. `useRegisterSW` — не читалка
 * состояния: при первом вызове он РЕГИСТРИРУЕТ service worker, а увидев
 * ожидающую новую сборку, вешает на неё слушателя `controlling`, который
 * перезагружает страницу (см. vite-plugin-pwa, режим prompt). Пока хук стоял
 * в самом баннере, он выполнялся и на маршрутах комнаты — проверка адреса шла
 * ПОСЛЕ него, а хук раньше проверки не отменишь. Вкладка с живой встречей
 * держала того самого слушателя: рекрутёр в соседней вкладке нажимал
 * «Обновить», новая сборка забирала все вкладки разом — и встреча
 * перезагружалась (инцидент 19.09.2026, вторая дверь к нему — разбор 20.09).
 *
 * Теперь хук живёт в дочернем компоненте, который на маршрутах комнаты не
 * монтируется вовсе: ни регистрации, ни слушателей, ни закачки всей CRM в
 * кэш на телефоне кандидата в первые секунды встречи.
 */
export default function PWAUpdatePrompt() {
  const [location] = useLocation();
  // Прячем баннер только в живой встрече. Список встреч — обычный рабочий
  // экран: там приложение работает как везде, включая предложение обновиться
  // (аудит 21.09.2026 — worker на нём уже встаёт, а баннер всё ещё прятался).
  if (isVideoLivePath(location)) return null;
  return <UpdateBanner />;
}

function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    needRefresh: [needRefresh],
  } = useRegisterSW();

  const update = async () => {
    setUpdating(true);
    setError(null);
    try {
      await applyPwaUpdate({
        pathname: () => window.location.pathname,
        registration: () => navigator.serviceWorker.getRegistration(),
        reload: () => window.location.reload(),
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось обновить страницу");
    } finally {
      setUpdating(false);
    }
  };

  if (!needRefresh || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[100] flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-lg md:left-auto md:right-6 md:max-w-xs">
      <p className="flex-1 text-[13px] font-medium text-slate-700">{error || "Доступно обновление"}</p>
      <button
        onClick={() => void update()}
        disabled={updating}
        className="shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-slate-800 transition-colors"
      >
        {updating ? "Обновляем…" : "Обновить"}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
        aria-label="Закрыть"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}
